import { alertToast } from '@/utilities/notification'

import { defineStore } from 'pinia'

import useApiRequest from '@/composables/useApiRequest'

import { useAuthStore } from './AuthStore'

// Types
interface UserSettings {
  darkmode: string
  locale: string
  timezone: string
  clock24Hours: boolean
}

export const useUserSettingsStore = defineStore('userSettingsStore', {
  state: (): {
    userSettings: UserSettings
  } => ({
    userSettings: JSON.parse(localStorage.getItem('userSettingsLK') ?? '{}') // LK = Last Known, ie. the settings which were set before a page refresh or session timeout
  }),

  getters: {
    /**
     * Gets darkmode setting
     */
    darkmode(): string {
      return this.userSettings.darkmode
    },

    /**
     * Gets locale setting
     */
    locale(): string {
      return this.userSettings.locale
    },

    /**
     * Gets timezone setting
     */
    timezone(): string {
      return this.userSettings.timezone
    },

    /**
     * Gets clock24Hours setting
     */
    clock24Hours(): boolean {
      return this.userSettings.clock24Hours
    }
  },

  actions: {
    /**
     * Sets all user settings from database
     * @param settingsFromDB - Settings object from database
     */
    setAllSettings(settingsFromDB: UserSettings): void {
      // Function sets user's settings which have been set from the DB. Only called on MainLayout on login check
      this.userSettings = settingsFromDB
      // Set in LS, so that if the user reloads the page, the last settings are initially set before getting actual settings from API. This will avoid a split second of incorrect settings.
      localStorage.setItem('userSettingsLK', JSON.stringify(settingsFromDB))
    },

    /**
     * Updates user settings
     * @param newSettings - Array of setting objects with name and value properties
     */
    async updateUserSettings(newSettings: Array<{ name: string; value: unknown }>): Promise<void> {
      const authStore = useAuthStore()

      if (!authStore.isLoggedIn || !authStore.details.id) {
        console.warn('Cannot update settings: user is not logged in')
        return
      }

      const currentState: UserSettings = { ...this.userSettings }
      const settingsObj: UserSettings = {
        darkmode: currentState.darkmode,
        locale: currentState.locale,
        timezone: currentState.timezone,
        clock24Hours: currentState.clock24Hours
      }

      // loop through each setting passed and update the state
      newSettings.forEach(setting => {
        const key = setting.name as keyof UserSettings
        ;(currentState as unknown as Record<string, unknown>)[key] = setting.value
        ;(settingsObj as unknown as Record<string, unknown>)[key] = setting.value
      })

      try {
        const updateSettingsApiRequest = useApiRequest()
        const response = await updateSettingsApiRequest.send({
          endpoint: '/v1/me/' + authStore.details.id + '/settings',
          method: 'PATCH',
          data: settingsObj
        })

        if (!response.success) {
          alertToast('Error', 'Failed updating settings', 'error')
          throw Error('Error patching data. Setting does not exist')
        }

        this.setAllSettings(currentState)
      } catch (err: unknown) {
        // Error handling is done by useApiRequest, but log for debugging
        if (err && typeof err === 'object' && 'response' in err) {
          const errorResponse = err as { response?: { status?: number; data?: unknown } }
          if (errorResponse.response) {
            console.error(errorResponse.response.status)
            console.error(errorResponse.response.data)
          }
        }
      }
    }
  }
})
